import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { ChildAuthService } from './child-auth.service';
import { TimeoutDialogComponent } from '../shared/components/timeout-dialog/timeout-dialog.component';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private readonly INACTIVITY_TIMEOUT = 60000; // 1 minute in milliseconds
  private readonly WARNING_DURATION = 30000; // 30 seconds in milliseconds
  private inactivityTimer: any;
  private warningTimer: any;
  private events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  private isDialogOpen = false;

  constructor(
    private ngZone: NgZone,
    private dialog: MatDialog,
    private router: Router,
    private authService: AuthService,
    private childAuthService: ChildAuthService
  ) {}

  /**
   * Start monitoring user activity
   */
  startMonitoring(): void {
    this.resetTimer();
    this.addEventListeners();
  }

  /**
   * Stop monitoring user activity
   */
  stopMonitoring(): void {
    this.clearTimers();
    this.removeEventListeners();
  }

  /**
   * Reset the inactivity timer
   */
  private resetTimer(): void {
    this.clearTimers();
    
    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      this.inactivityTimer = setTimeout(() => {
        // When timeout occurs, run back inside Angular zone
        this.ngZone.run(() => {
          this.handleInactivity();
        });
      }, this.INACTIVITY_TIMEOUT);
    });
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
  }

  /**
   * Add event listeners to track user activity
   */
  private addEventListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      this.events.forEach(event => {
        window.addEventListener(event, this.resetTimerBound);
      });
    });
  }

  /**
   * Remove event listeners
   */
  private removeEventListeners(): void {
    this.events.forEach(event => {
      window.removeEventListener(event, this.resetTimerBound);
    });
  }

  /**
   * Bound version of resetTimer to use with event listeners
   */
  private resetTimerBound = () => this.resetTimer();

  /**
   * Handle inactivity timeout
   */
  private async handleInactivity(): Promise<void> {
    // Don't show dialog if one is already open
    if (this.isDialogOpen) {
      return;
    }

    // Check if user is logged in
    const user = await this.authService.getCurrentUser();
    if (!user) {
      return; // No action needed if no user is logged in
    }

    // Check if in child mode with a logged in child
    const childState = this.childAuthService.getLoggedInChild();
    const isChildMode = this.childAuthService.isChildMode();

    // Skip inactivity handling if in child mode but no child is logged in
    if (isChildMode && !childState) {
      return; // No action needed if in child mode but no child logged in
    }

    // Open warning dialog
    this.isDialogOpen = true;
    const dialogRef = this.dialog.open(TimeoutDialogComponent, {
      width: '300px',
      disableClose: true,
      data: {
        title: 'Are you still there?',
        message: isChildMode && childState 
          ? 'You will be logged out in 30 seconds due to inactivity.'
          : 'You will be switched to child mode in 30 seconds due to inactivity.'
      }
    });

    // Start warning timer
    this.warningTimer = setTimeout(() => {
      dialogRef.close(false);
    }, this.WARNING_DURATION);

    // Handle dialog result
    const result = await firstValueFrom(dialogRef.afterClosed());
    this.isDialogOpen = false;
    
    if (result) {
      // User clicked "Stay Active"
      clearTimeout(this.warningTimer);
      this.resetTimer();
    } else {
      // User didn't respond in time or clicked "Log Out"
      if (isChildMode && childState) {
        // Logout child but stay in child mode
        this.childAuthService.logoutChildStayInChildMode();
      } else if (!isChildMode) {
        // Only switch to child mode if currently in parent mode
        this.childAuthService.toggleChildMode();
      }
    }
  }
}
